import { useModalState } from "@src/lib/ui-react";
import { AppV2Routes } from "@src/appV2";
import { cbhQueryClient } from "@src/appV2/api/queryClient";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";
import { type SessionInitOutcome } from "@src/appV2/Auth/cognito/initCognitoSession";
import { Login } from "@src/appV2/redesign/Login/Login";
import { QueryClientProvider } from "@tanstack/react-query";
import { HomeHealthProtectedCasesPage } from "@src/appV2/ExperimentalHomeHealth/Cases/ProtectedCasesPage";
import { HomeHealthProtectedVisitVerificationPage } from "@src/appV2/ExperimentalHomeHealth/VisitVerification/ProtectedVisitVerificationPage";
import { ProtectedMobileVisitVerificationPage } from "@src/appV2/redesign/HomeHealth/VisitVerification/ProtectedMobileVisitVerificationPage";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { useIsHomeHealthWorkplaceMobileExperienceEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthWorkplaceMobileExperienceEnabled";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import { Suspense, useEffect, useRef, useState } from "react";
import { matchPath, Redirect, Route, Switch, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { type SessionType } from "@src/modules/interface";

import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import {
  getHomeRouteForSession,
  getSessionRoleFromFlags,
} from "./Shared/AccountSwitcher/getHomeRouteForSession";
import { AdminMenuPrivilegesProvider } from "./Shared/Navigation/privilegesProviders/AdminMenuPrivilegesProvider";
import { WorkplaceMenuPrivilegesProvider } from "./Shared/Navigation/privilegesProviders/WorkplaceMenuPrivilegesProvider";
import { MaterialUiProviders } from "@src/mui/providers/MaterialUiProviders";
import { ConnectedOmnisearch } from "@src/containers/app/omnisearch";
import { useReloadPageOnUserSwitch } from "@src/containers/app/hooks/useReloadPageOnUserSwitch";
import { AppLayoutDesktop } from "./Desktop/AppLayoutDesktop";
import { Box, type Theme, useMediaQuery } from "@mui/material";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { useIsRedesignEnabled } from "@src/appV2/redesign/hooks/useIsRedesignEnabled";
import { AppLayoutMobile } from "./Mobile/AppLayoutMobile";
import { WorkerMenuPrivilegesProvider } from "./Shared/Navigation/privilegesProviders/WorkerMenuPrivilegesProvider";
import { FloatingTabsProvider } from "@src/appV2/redesign/AppLayout/Mobile/FloatingTabsContext";
import { LogoutConfirmationDialog } from "./Shared/LogoutConfirmationDialog";
import { HomeHealthMobileUnsupportedPage } from "./Shared/HomeHealthMobileUnsupportedPage";
import { AppLayoutTablet } from "./Tablet/Navigation/AppLayoutTablet";
import { BrazeInitializedProvider } from "@src/appV2/redesign/AppLayout/Shared/BrazeContext";
import { ModalsContainerProvider } from "@src/appV2/redesign/components/useModalsContainerContext";
import { Platform } from "@src/mobile/utils/platform";
import { ClipboardLogo } from "@src/appV2/redesign/components/Logos";
import { WorkplaceAgreementDialog } from "@src/appV2/WorkplaceAgreement/WorkplaceAgreementDialog";
import { useInitializeMonitoring } from "src/hooks";
import { ThemeModeToggle } from "@src/appV2/UserPreferences/components/ThemeModeToggle";
import { EnvironmentChip } from "./Shared/Header/EnvironmentChip";
import { FullScreenLoader } from "./FullScreenLoader";
import {
  canKeepAuthenticatedLayoutMountedDuringSessionRefresh,
  isAuthOnlyRoute,
  shouldBypassAuthenticatedSessionEffects,
} from "./NavigationMenuContainerGuards";

const PUBLIC_HIRING_HEADER_HEIGHT = "3.875rem";
const PUBLIC_HIRING_HEADER_SAFE_AREA_TOP = "env(safe-area-inset-top, 0px)";
const PUBLIC_HIRING_HEADER_OFFSET = `${PUBLIC_HIRING_HEADER_HEIGHT} + ${PUBLIC_HIRING_HEADER_SAFE_AREA_TOP}`;

function PublicHiringHeader() {
  return (
    <BerlinThemeProvider>
      <Box
        sx={(theme) => ({
          background: theme.palette.navbar?.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxSizing: "border-box",
          height: `calc(${PUBLIC_HIRING_HEADER_OFFSET})`,
          paddingLeft: 5,
          paddingRight: 5,
          paddingTop: PUBLIC_HIRING_HEADER_SAFE_AREA_TOP,
          flexShrink: 0,
        })}
      >
        <Box sx={{ display: "inline-flex", alignItems: "flex-start", gap: 1 }}>
          <ClipboardLogo height={30} width={100} />
          <EnvironmentChip />
        </Box>
        <ThemeModeToggle />
      </Box>
    </BerlinThemeProvider>
  );
}

/**
 * Hiring route patterns accessible without authentication.
 * These correspond to the plain <Route> entries in AllRoutes.tsx (lines 1178-1211).
 */
const PUBLIC_HIRING_ROUTE_PATTERNS: string[] = [
  routes.publicWorkplacePlacements,
  routes.workplaceHireProfessionalDetails,
  routes.workplaceJobApplicantDetails,
];

function isPublicHiringRoute(pathname: string): boolean {
  return PUBLIC_HIRING_ROUTE_PATTERNS.some((pattern) =>
    matchPath(pathname, { path: pattern, exact: true })
  );
}

/**
 * Routes that the legacy AppLayout exposed as plain <Route> entries with no
 * session required. Each one is meant for external visitors (QR scanners,
 * third-party referees, recipients of invoice email links, marketing landing)
 * and the component handles its own auth — via URL-embedded token or an
 * unauthenticated public endpoint. Listing them here keeps NavigationMenuContainer's
 * auth gates from redirecting those visitors to /login.
 */
const PUBLIC_LEGACY_ROUTE_PATTERNS: string[] = [
  routes.shiftSignoutFeedback,
  routes.reference,
  routes.timesheet,
  routes.workers,
  routes.appDownload,
];

function isPublicLegacyRoute(pathname: string): boolean {
  return PUBLIC_LEGACY_ROUTE_PATTERNS.some((pattern) =>
    matchPath(pathname, { path: pattern, exact: true })
  );
}

interface NavigationMenuContainerProps {
  admin: boolean;
  isAdmin: boolean;
  isAgent: boolean;
  isFacility: boolean;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  session: any;
  get: () => void;
  getAdminProfile: () => void;
  loadingAdminProfile: boolean;
  initCognitoSession: () => Promise<SessionInitOutcome>;
  getLocations: () => void;
  getEmployees: () => void;
  isIdentifiedForCurrentUser: boolean;
}

const AdminAppLogin = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.AdminAppLogin,
    }))
);

const ShiftSignoutFeedbackForm = loadLazily(
  async () =>
    await import("@src/containers/ShiftSignoutFlow/shiftSignoutFlowForm").then((module) => ({
      default: module.ShiftSignoutFlowForm,
    }))
);

const ReferenceForm = loadLazily(
  async () =>
    await import("@src/containers/referenceForm").then((module) => ({
      default: module.ReferenceForm,
    }))
);

const TimesheetView = loadLazily(
  async () =>
    await import("@src/containers/accountManagement/invoice/timesheetView").then((module) => ({
      default: module.TimesheetView,
    }))
);

const WorkerLanding = loadLazily(async () => await import("@src/containers/workerLanding"));

const AppDownloadRedirect = loadLazily(
  async () =>
    await import("@src/appV2/redesign/AppDownload/AppDownloadRedirect").then((module) => ({
      default: module.AppDownloadRedirect,
    }))
);

const PublicWorkplacePlacementsPage = loadLazily(
  async () =>
    await import("@src/appV2/PublicWorkplacePlacements/Page").then((module) => ({
      default: module.PublicWorkplacePlacementsPage,
    }))
);

const PlacementCandidateDetailsPage = loadLazily(
  async () =>
    await import("@src/appV2/PlacementCandidates/PlacementCandidateDetailsPage").then((module) => ({
      default: module.PlacementCandidateDetailsPage,
    }))
);

const UnverifiedPlacementCandidateApplicationPage = loadLazily(
  async () =>
    await import("@src/appV2/UnverifiedPlacementCandidateApplication/UnverifiedPlacementCandidateApplication").then(
      (module) => ({
        default: module.UnverifiedPlacementCandidateApplicationPage,
      })
    )
);

/**
 * Below this width the navigation collapses to icon-only mode.
 * At or above this width, the full text navigation sidebar is shown.
 */
const NAV_EXPAND_THRESHOLD_PX = 1500;

export function NavigationMenuContainer(props: NavigationMenuContainerProps): JSX.Element {
  return (
    <BerlinThemeProvider fullHeight>
      <NavigationMenuContainerContent {...props} />
    </BerlinThemeProvider>
  );
}

function NavigationMenuContainerContent(props: NavigationMenuContainerProps): JSX.Element {
  const {
    admin,
    isAdmin,
    isAgent: isWorker,
    isFacility: isWorkplace,
    session,
    get,
    getAdminProfile,
    loadingAdminProfile,
    initCognitoSession,
    getLocations,
    getEmployees,
    isIdentifiedForCurrentUser,
  } = props;

  useInitializeMonitoring();

  const logoutDialog = useModalState();
  const logoutFromAllDevicesDialog = useModalState();

  const [loading, setLoading] = useState(true);
  const { ready, profile, user } = useSelector((state: SessionType) => state.session || {});
  const { access } = user ?? {};
  const isAuthorized = isAdmin || isWorker || isWorkplace;

  const location = useLocation();
  const shouldBypassSessionEffects = shouldBypassAuthenticatedSessionEffects(location.pathname);

  const isMobile = useIsMobile();
  const isRedesignEnabled = useIsRedesignEnabled();
  const isNavCollapsed = useMediaQuery((theme: Theme) =>
    theme.breakpoints.between("md", NAV_EXPAND_THRESHOLD_PX)
  );

  // Home Health check for start page redirect
  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const { hasDefaultWorkplacePermissions, isInPseudoSession } = useEffectivePermissions();
  const isHomeHealthWorkplaceMobileEnabled = useIsHomeHealthWorkplaceMobileExperienceEnabled();

  // Reload the page when the user switches on routes that have stale data bugs.
  // This matches the legacy AppLayout behavior (appLayout.tsx line 827).
  useReloadPageOnUserSwitch();

  // Initialize admin profile
  useEffect(() => {
    if (shouldBypassSessionEffects) {
      return;
    }

    if (loadingAdminProfile) {
      getAdminProfile();
    }
  }, [loadingAdminProfile, getAdminProfile, shouldBypassSessionEffects]);

  // Track when we transition from a public auth route (e.g. /v2/email-login-link → /)
  // so that session init can re-run to hydrate newly stored Cognito tokens.
  const [sessionInitCounter, setSessionInitCounter] = useState(0);
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    const wasAuthRoute = prevPathnameRef.current.startsWith(routes.appV2);
    prevPathnameRef.current = location.pathname;

    if (wasAuthRoute && !location.pathname.startsWith(routes.appV2)) {
      setSessionInitCounter((count) => count + 1);
    }
  }, [location.pathname]);

  // Initialize Cognito session. Runs on mount and when sessionInitCounter
  // increments (i.e. after an auth route transition), but NOT on every
  // pathname change.
  useEffect(() => {
    if (shouldBypassSessionEffects) {
      return;
    }

    if (!isIdentifiedForCurrentUser) {
      return;
    }

    async function load() {
      setLoading(true);
      await initCognitoSession();
      setLoading(false);
    }
    void load();
  }, [
    initCognitoSession,
    sessionInitCounter,
    isIdentifiedForCurrentUser,
    shouldBypassSessionEffects,
  ]);

  // Fetch session data and locations
  useEffect(() => {
    if (shouldBypassSessionEffects) {
      return;
    }

    if (loading || !ready) {
      return;
    }
    if (!isAuthorized) {
      return;
    }
    get();
    getLocations();

    if (session?.admin) {
      getEmployees();
    }
  }, [
    isAuthorized,
    get,
    getLocations,
    getEmployees,
    loading,
    ready,
    session?.admin,
    shouldBypassSessionEffects,
  ]);

  function renderStartPage() {
    const role = getSessionRoleFromFlags({ isAdmin, isWorker, isWorkplace });

    if (!role) {
      return null;
    }

    if (role === "workplace") {
      if (!isHomeHealthEnabled && !hasDefaultWorkplacePermissions) {
        return null;
      }

      return (
        <Redirect
          to={getHomeRouteForSession({
            role,
            isMobile: isMobile || Platform.isMobile,
            isHomeHealthEnabled,
            isHomeHealthWorkplaceMobileEnabled,
            canAccessDailyView: isRedesignEnabled && hasDefaultWorkplacePermissions,
          })}
        />
      );
    }

    return <Redirect to={getHomeRouteForSession({ role })} />;
  }

  function renderAppLayout() {
    if (isMobile) {
      return (
        <AppLayoutMobile
          isAdmin={isAdmin}
          isWorker={isWorker}
          admin={admin}
          isWorkplace={isWorkplace}
          userName={profile.name}
          userId={profile.userId}
          logoutConfirm={logoutDialog.openModal}
          logoutFromAllDevicesConfirm={logoutFromAllDevicesDialog.openModal}
          showLogoutFromAllDevices={!isInPseudoSession}
          renderStartPage={renderStartPage}
        />
      );
    }

    if (isNavCollapsed) {
      return (
        <AppLayoutTablet
          isAdmin={isAdmin}
          isWorker={isWorker}
          admin={admin}
          isWorkplace={isWorkplace}
          userName={profile.name}
          userId={profile.userId}
          logoutConfirm={logoutDialog.openModal}
          logoutFromAllDevicesConfirm={logoutFromAllDevicesDialog.openModal}
          showLogoutFromAllDevices={!isInPseudoSession}
          renderStartPage={renderStartPage}
        />
      );
    }

    return (
      <AppLayoutDesktop
        admin={admin}
        isAdmin={isAdmin}
        isWorker={isWorker}
        isWorkplace={isWorkplace}
        userName={profile.name}
        userId={profile.userId}
        logoutConfirm={logoutDialog.openModal}
        logoutFromAllDevicesConfirm={logoutFromAllDevicesDialog.openModal}
        showLogoutFromAllDevices={!isInPseudoSession}
        renderStartPage={renderStartPage}
      />
    );
  }

  // Redirect users to authenticated screen in case they visit the login screen
  if (isAuthorized && location.pathname === routes.login) {
    return renderStartPage() ?? <FullScreenLoader />;
  }

  // Early return for routes that must render regardless of auth state.
  // These routes handle their own auth redirects (e.g. to /enter?requestedUrl=...)
  // and must bypass NavigationMenuContainer's auth guards which redirect to /login.
  const isPreAuthRoute =
    location.pathname === routes.login ||
    isAuthOnlyRoute(location.pathname) ||
    isPublicLegacyRoute(location.pathname) ||
    (location.pathname.startsWith(routes.appV2) && !isAuthorized) ||
    (location.pathname.startsWith("/home-health") && !isAuthorized);

  if (isPreAuthRoute) {
    return (
      <Suspense fallback={<div style={{ height: "100vh" }} />}>
        <div
          style={{
            height: "100vh",
            paddingTop: "env(safe-area-inset-top, 0px)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            boxSizing: "border-box",
          }}
        >
          <Route
            exact
            path={routes.login}
            component={isMobile || isRedesignEnabled ? Login : AdminAppLogin}
          />
          <Route path={routes.shiftSignoutFeedback} component={ShiftSignoutFeedbackForm} />
          <Route path={routes.reference} component={ReferenceForm} />
          <Route exact path={routes.timesheet} component={TimesheetView} />
          <Route exact path={routes.workers} component={WorkerLanding} />
          <Route exact path={routes.appDownload} component={AppDownloadRedirect} />
          <Route path={routes.appV2}>
            <QueryClientProvider client={cbhQueryClient}>
              <AppV2Routes />
            </QueryClientProvider>
          </Route>
          <Route path={routes.homeHealthCases} component={HomeHealthProtectedCasesPage} />
          <Route
            exact
            path={routes.homeHealthVisitVerificationMobile}
            component={ProtectedMobileVisitVerificationPage}
          />
          <Route
            path={routes.homeHealthVisitVerification}
            component={HomeHealthProtectedVisitVerificationPage}
          />
        </div>
      </Suspense>
    );
  }

  // Public hiring routes: accessible without authentication.
  // For unauthenticated users, render route content without the authenticated sidebar/header.
  // Authenticated users fall through to the normal layout with sidebar.
  if (!isAuthorized && !loading && isPublicHiringRoute(location.pathname)) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <QueryClientProvider client={cbhQueryClient}>
          <MaterialUiProviders>
            <PublicHiringHeader />
            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.background.default,
                padding: 3,
                minHeight: `calc(100vh - ${PUBLIC_HIRING_HEADER_OFFSET})`,
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
                [theme.breakpoints.down("md")]: {
                  padding: 0,
                  paddingBottom: "env(safe-area-inset-bottom, 0px)",
                },
              })}
            >
              <Switch>
                <Route
                  exact
                  path={routes.publicWorkplacePlacements}
                  component={PublicWorkplacePlacementsPage}
                />
                <Route
                  exact
                  path={routes.workplaceHireProfessionalDetails}
                  component={PlacementCandidateDetailsPage}
                />
                <Route
                  exact
                  path={routes.workplaceJobApplicantDetails}
                  component={UnverifiedPlacementCandidateApplicationPage}
                />
              </Switch>
            </Box>
          </MaterialUiProviders>
        </QueryClientProvider>
      </Suspense>
    );
  }

  // Guard to prevent rendering while session is initializing
  // This matches the old AppLayout logic (line 918 in appLayout.tsx)
  // Prevents API calls from firing before auth token and profile data are ready
  //
  // Important: ready becomes true BEFORE fetchSession() completes loading the full profile
  // We need to wait for profile.tmz to exist as it's required by API calls in AgentDashboard
  const hasRequiredProfileData = Boolean(profile?.userId);
  const canKeepAuthenticatedLayoutMounted = canKeepAuthenticatedLayoutMountedDuringSessionRefresh({
    access,
    admin,
    hasRequiredProfileData,
    isWorkplace,
  });

  if ((loading || !ready) && session.userId && !canKeepAuthenticatedLayoutMounted) {
    return <FullScreenLoader />;
  }

  // If user is logged out (no profile data and no session loading), redirect to login.
  // This prevents users from getting stuck on a blank page after logout.
  // Skip the redirect when Cognito tokens exist in localStorage — the session init
  // effect is about to hydrate them into Redux (e.g. after AuthRedirect stores tokens
  // and navigates here).
  if (!hasRequiredProfileData && !loading) {
    const hasCognitoTokens = Object.keys(localStorage).some((key) =>
      key.startsWith("CognitoIdentityServiceProvider")
    );
    if (!hasCognitoTokens) {
      return <Redirect to="/login" />;
    }
  }

  // Additional guard: ensure profile data is fully loaded before rendering
  // AgentDashboard needs profile.tmz, profile.geoLocation, profile.preference, etc.
  if (!hasRequiredProfileData) {
    return <FullScreenLoader />;
  }

  // Guard for workplace users: wait for roles (access) to be loaded
  // This prevents a redirect loop where PrivateRoute redirects to /enter because
  // isAuthorized is false (roles not loaded), and /enter redirects back to /facility/dashboard
  // This matches the guard in legacy appLayout.tsx (line 975-977)
  if (isWorkplace && !admin && access === undefined) {
    return <FullScreenLoader />;
  }

  // Home Health workplace users on mobile (native Capacitor or narrow
  // viewport) are blocked from the app — HH workplace surfaces aren't
  // designed for mobile yet, and the previous routing produced a redirect
  // loop (TECH-1370). All in-app paths are intercepted to a dead-end page
  // with only a logout action. Admin pseudo sessions (`admin` true) are
  // excluded so internal users can still inspect HH workplaces on mobile.
  // The HOME_HEALTH_WORKPLACE_MOBILE_EXPERIENCE_ENABLED flag lifts the
  // block once the mobile experience is ready to ship to workplace users.
  const isHomeHealthMobileBlocked =
    isWorkplace &&
    !admin &&
    isHomeHealthEnabled &&
    !isHomeHealthWorkplaceMobileEnabled &&
    (isMobile || Platform.isMobile);
  if (isHomeHealthMobileBlocked) {
    if (location.pathname !== routes.homeHealthMobileUnsupported) {
      return <Redirect to={routes.homeHealthMobileUnsupported} />;
    }
    return <HomeHealthMobileUnsupportedPage />;
  }

  return (
    <ModalsContainerProvider>
      <BrazeInitializedProvider isAdminSession={admin} isWorkplace={isWorkplace}>
        <AdminMenuPrivilegesProvider>
          <WorkplaceMenuPrivilegesProvider>
            <WorkerMenuPrivilegesProvider>
              <FloatingTabsProvider>
                <ConnectedOmnisearch>{renderAppLayout()}</ConnectedOmnisearch>
                <LogoutConfirmationDialog modalState={logoutDialog} />
                <LogoutConfirmationDialog
                  modalState={logoutFromAllDevicesDialog}
                  mode="all-devices"
                />
                <WorkplaceAgreementDialog />
              </FloatingTabsProvider>
            </WorkerMenuPrivilegesProvider>
          </WorkplaceMenuPrivilegesProvider>
        </AdminMenuPrivilegesProvider>
      </BrazeInitializedProvider>
    </ModalsContainerProvider>
  );
}
